import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  InputAdornment,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Handshake,
  Add,
  PlayArrow,
  Pause,
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  Psychology,
  Lightbulb,
  Warning,
  Info,
  History,
  Visibility,
  AttachMoney,
  LocalShipping,
  Schedule,
  Assessment,
  Refresh,
  Help,
  Email,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatUKDate, formatGBP } from '../../utils/ukFormatting';
import { startNegotiation, getNegotiationSession, NegotiationResponse, NegotiationRequest } from '../../api/langgraph';

interface NegotiationSession {
  id: string;
  supplier_id: number;
  supplier_name: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'canceled';
  created_at: string;
  products: NegotiationProduct[];
  target_savings: number;
  actual_savings: number;
  rounds_completed: number;
  current_offer?: NegotiationOffer;
  ai_recommendation?: string;
}

interface NegotiationProduct {
  product_id: number;
  product_name: string;
  current_price: number;
  target_price: number;
  final_price?: number;
  quantity: number;
}

interface NegotiationOffer {
  round: number;
  our_offer: number;
  their_counter?: number;
  status: 'pending' | 'accepted' | 'rejected' | 'countered';
  timestamp: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Mock data for demonstration
const mockSessions: NegotiationSession[] = [
  {
    id: 'NEG-001',
    supplier_id: 1,
    supplier_name: 'TechParts Global',
    status: 'active',
    created_at: '2025-11-28T10:00:00Z',
    products: [
      { product_id: 1, product_name: 'CPU Module A', current_price: 150, target_price: 135, quantity: 500 },
      { product_id: 2, product_name: 'Memory Chip B', current_price: 45, target_price: 40, quantity: 1000 },
    ],
    target_savings: 12500,
    actual_savings: 8750,
    rounds_completed: 2,
    current_offer: { round: 3, our_offer: 138, status: 'pending', timestamp: '2025-11-30T09:00:00Z' },
    ai_recommendation: 'Supplier showing flexibility. Consider bundling Volume Discount request with extended payment terms.',
  },
  {
    id: 'NEG-002',
    supplier_id: 2,
    supplier_name: 'Pacific Components',
    status: 'completed',
    created_at: '2025-11-20T08:00:00Z',
    products: [
      { product_id: 3, product_name: 'Power Supply Unit', current_price: 85, target_price: 75, final_price: 78, quantity: 200 },
    ],
    target_savings: 2000,
    actual_savings: 1400,
    rounds_completed: 4,
  },
  {
    id: 'NEG-003',
    supplier_id: 3,
    supplier_name: 'Euro Electronics',
    status: 'draft',
    created_at: '2025-11-30T14:00:00Z',
    products: [
      { product_id: 4, product_name: 'Sensor Array', current_price: 220, target_price: 195, quantity: 100 },
    ],
    target_savings: 2500,
    actual_savings: 0,
    rounds_completed: 0,
  },
];

const mockSuppliers = [
  { id: 1, name: 'TechParts Global', reliability: 94, avg_lead_time: 12 },
  { id: 2, name: 'Pacific Components', reliability: 91, avg_lead_time: 18 },
  { id: 3, name: 'Euro Electronics', reliability: 88, avg_lead_time: 21 },
  { id: 4, name: 'Asian Microtech', reliability: 85, avg_lead_time: 25 },
];

const mockProducts = [
  { id: 1, name: 'CPU Module A', sku: 'CPU-A-001', current_price: 150 },
  { id: 2, name: 'Memory Chip B', sku: 'MEM-B-002', current_price: 45 },
  { id: 3, name: 'Power Supply Unit', sku: 'PSU-001', current_price: 85 },
  { id: 4, name: 'Sensor Array', sku: 'SEN-ARR-01', current_price: 220 },
  { id: 5, name: 'Display Panel', sku: 'DSP-PNL-01', current_price: 180 },
];

const NegotiationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<NegotiationSession[]>(mockSessions);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<NegotiationSession | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  
  // API state
  const [isLoading, setIsLoading] = useState(false);
  const [apiResult, setApiResult] = useState<NegotiationResponse | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  
  // New negotiation form state
  const [newNegotiation, setNewNegotiation] = useState({
    supplier_id: '',
    supplier_name: '',
    products: [] as { product_id: number; quantity: number; target_price: number; current_price: number }[],
    batna: '',
    strategy: 'balanced',
    auto_negotiate: false,
  });

  // Handler to call LangGraph API
  const handleStartAINegotiation = async () => {
    if (!newNegotiation.supplier_id) {
      setSnackbar({ open: true, message: 'Please select a supplier', severity: 'error' });
      return;
    }

    const selectedSupplier = mockSuppliers.find(s => s.id === Number(newNegotiation.supplier_id));
    if (!selectedSupplier) return;

    // Get the first product's prices (or calculate totals if multiple products)
    const totalCurrentPrice = newNegotiation.products.reduce(
      (sum, p) => sum + (p.current_price || mockProducts.find(mp => mp.id === p.product_id)?.current_price || 100) * p.quantity,
      0
    ) || 1000;
    const totalTargetPrice = newNegotiation.products.reduce(
      (sum, p) => sum + p.target_price * p.quantity,
      0
    ) || 850;
    const totalVolume = newNegotiation.products.reduce((sum, p) => sum + p.quantity, 0) || 1000;

    setIsLoading(true);
    setDialogOpen(false);

    try {
      const request: NegotiationRequest = {
        supplier_id: `sup-${newNegotiation.supplier_id}`,
        supplier_name: selectedSupplier.name,
        target_price: totalTargetPrice,
        current_price: totalCurrentPrice,
        volume: totalVolume,
        category: 'electronics',
        batna: newNegotiation.batna || undefined,
        max_rounds: 5,
      };

      const result = await startNegotiation(request);
      setApiResult(result);
      setResultDialogOpen(true);
      setSnackbar({ open: true, message: 'AI Negotiation completed!', severity: 'success' });

      // Add to sessions list
      const newSession: NegotiationSession = {
        id: result.session_id,
        supplier_id: Number(newNegotiation.supplier_id),
        supplier_name: selectedSupplier.name,
        status: result.status === 'completed' ? 'completed' : 'active',
        created_at: new Date().toISOString(),
        products: newNegotiation.products.map(p => ({
          product_id: p.product_id,
          product_name: mockProducts.find(mp => mp.id === p.product_id)?.name || 'Product',
          current_price: p.current_price || 100,
          target_price: p.target_price,
          final_price: result.final_price || undefined,
          quantity: p.quantity,
        })),
        target_savings: totalCurrentPrice - totalTargetPrice,
        actual_savings: result.final_price ? totalCurrentPrice - result.final_price : 0,
        rounds_completed: result.rounds_count,
        ai_recommendation: result.next_steps.join('. '),
      };

      setSessions([newSession, ...sessions]);
    } catch (error) {
      console.error('Negotiation error:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error starting negotiation. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setIsLoading(false);
      setActiveStep(0);
      setNewNegotiation({
        supplier_id: '',
        supplier_name: '',
        products: [],
        batna: '',
        strategy: 'balanced',
        auto_negotiate: false,
      });
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
      draft: 'default',
      active: 'info',
      paused: 'warning',
      completed: 'success',
      canceled: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    const icons: Record<string, React.ReactElement> = {
      draft: <Info />,
      active: <PlayArrow />,
      paused: <Pause />,
      completed: <CheckCircle />,
      canceled: <Cancel />,
    };
    return icons[status] || <Info />;
  };

  const handleViewDetails = (session: NegotiationSession) => {
    setSelectedSession(session);
    setDetailDialogOpen(true);
  };

  const handleStartNegotiation = (sessionId: string) => {
    setSessions(sessions.map(s => 
      s.id === sessionId ? { ...s, status: 'active' as const } : s
    ));
  };

  const handlePauseNegotiation = (sessionId: string) => {
    setSessions(sessions.map(s => 
      s.id === sessionId ? { ...s, status: 'paused' as const } : s
    ));
  };

  const calculateTotalValue = (products: NegotiationProduct[]) => {
    return products.reduce((sum, p) => sum + p.current_price * p.quantity, 0);
  };

  const calculateTargetValue = (products: NegotiationProduct[]) => {
    return products.reduce((sum, p) => sum + p.target_price * p.quantity, 0);
  };

  const activeSessions = sessions.filter(s => s.status === 'active' || s.status === 'paused');
  const completedSessions = sessions.filter(s => s.status === 'completed' || s.status === 'canceled');
  const draftSessions = sessions.filter(s => s.status === 'draft');

  const totalSavings = sessions
    .filter(s => s.status === 'completed')
    .reduce((sum, s) => sum + s.actual_savings, 0);

  const steps = ['Select Supplier', 'Add Products', 'Set Strategy', 'Review & Start'];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          <Handshake sx={{ mr: 1, verticalAlign: 'middle' }} />
          AI-Powered Negotiations
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Psychology />}
            onClick={() => navigate('/negotiations/hitl')}
            sx={{ mr: 2 }}
          >
            HITL Mode
          </Button>
          <Button
            variant="outlined"
            startIcon={<Help />}
            onClick={() => navigate('/help')}
            sx={{ mr: 2 }}
          >
            How It Works
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            New Negotiation
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Active Negotiations</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                {activeSessions.length}
              </Typography>
              <Chip label={`${draftSessions.length} drafts`} size="small" sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Completed This Month</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {completedSessions.length}
              </Typography>
              <Chip label="100% success" size="small" color="success" sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Total Savings Achieved</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                ${totalSavings.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp color="success" fontSize="small" />
                <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>+23% vs target</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Avg. Negotiation Time</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                3.2 days
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingDown color="success" fontSize="small" />
                <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>-40% with AI</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* AI Insight Banner */}
      {activeSessions.length > 0 && activeSessions[0].ai_recommendation && (
        <Alert 
          severity="info" 
          icon={<Psychology />}
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => handleViewDetails(activeSessions[0])}>
              View Details
            </Button>
          }
        >
          <AlertTitle>AI Recommendation for {activeSessions[0].supplier_name}</AlertTitle>
          {activeSessions[0].ai_recommendation}
        </Alert>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={`Active (${activeSessions.length})`} />
          <Tab label={`Drafts (${draftSessions.length})`} />
          <Tab label={`History (${completedSessions.length})`} />
        </Tabs>

        {/* Active Sessions */}
        <TabPanel value={tabValue} index={0}>
          {activeSessions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Handshake sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No active negotiations</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Start a new negotiation or resume a draft
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
                New Negotiation
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Session</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Products</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Target Savings</TableCell>
                    <TableCell align="right">Progress</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeSessions.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{session.id}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Round {session.rounds_completed + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>{session.supplier_name}</TableCell>
                      <TableCell>
                        {session.products.map(p => p.product_name).join(', ')}
                        <Typography variant="caption" display="block" color="text.secondary">
                          Total: ${calculateTotalValue(session.products).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={getStatusIcon(session.status)} 
                          label={session.status} 
                          color={getStatusColor(session.status)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          ${session.target_savings.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={(session.actual_savings / session.target_savings) * 100} 
                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">
                            {Math.round((session.actual_savings / session.target_savings) * 100)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(session)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {session.status === 'active' ? (
                          <Tooltip title="Pause">
                            <IconButton size="small" onClick={() => handlePauseNegotiation(session.id)}>
                              <Pause />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Resume">
                            <IconButton size="small" color="primary" onClick={() => handleStartNegotiation(session.id)}>
                              <PlayArrow />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Draft Sessions */}
        <TabPanel value={tabValue} index={1}>
          {draftSessions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No draft negotiations</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Session</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Products</TableCell>
                    <TableCell align="right">Target Savings</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {draftSessions.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>{session.id}</TableCell>
                      <TableCell>{session.supplier_name}</TableCell>
                      <TableCell>{session.products.map(p => p.product_name).join(', ')}</TableCell>
                      <TableCell align="right">{formatGBP(session.target_savings)}</TableCell>
                      <TableCell>{formatUKDate(session.created_at)}</TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          variant="contained" 
                          startIcon={<PlayArrow />}
                          onClick={() => handleStartNegotiation(session.id)}
                        >
                          Start
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* History */}
        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Session</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Target</TableCell>
                  <TableCell align="right">Achieved</TableCell>
                  <TableCell>Rounds</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completedSessions.map((session) => (
                  <TableRow key={session.id} hover>
                    <TableCell>{session.id}</TableCell>
                    <TableCell>{session.supplier_name}</TableCell>
                    <TableCell>
                      <Chip 
                        icon={getStatusIcon(session.status)} 
                        label={session.status} 
                        color={getStatusColor(session.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="right">{formatGBP(session.target_savings)}</TableCell>
                    <TableCell align="right">
                      <Typography 
                        color={session.actual_savings >= session.target_savings ? 'success.main' : 'warning.main'}
                        fontWeight={600}
                      >
                        {formatGBP(session.actual_savings)}
                      </Typography>
                    </TableCell>
                    <TableCell>{session.rounds_completed}</TableCell>
                    <TableCell>{formatUKDate(session.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* New Negotiation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Handshake color="primary" />
            Create New Negotiation
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ my: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Select the supplier you want to negotiate with:</Typography>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={newNegotiation.supplier_id}
                  onChange={(e) => setNewNegotiation({ ...newNegotiation, supplier_id: e.target.value as string })}
                  label="Supplier"
                >
                  {mockSuppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <span>{s.name}</span>
                        <Chip label={`${s.reliability}% reliable`} size="small" color="success" />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Alert severity="info">
                <AlertTitle>Tip: Supplier Selection</AlertTitle>
                Choose suppliers where you have leverage (volume, alternatives) for best negotiation outcomes.
              </Alert>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Select products to include in this negotiation:</Typography>
              <List>
                {mockProducts.map((product) => (
                  <ListItem key={product.id} divider>
                    <ListItemText 
                      primary={product.name} 
                      secondary={`SKU: ${product.sku} | Current: $${product.current_price}`} 
                    />
                    <TextField
                      label="Quantity"
                      type="number"
                      size="small"
                      sx={{ width: 100, mr: 2 }}
                    />
                    <TextField
                      label="Target Price"
                      type="number"
                      size="small"
                      sx={{ width: 120 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Configure your negotiation strategy:</Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Strategy Approach</InputLabel>
                <Select
                  value={newNegotiation.strategy}
                  onChange={(e) => setNewNegotiation({ ...newNegotiation, strategy: e.target.value })}
                  label="Strategy Approach"
                >
                  <MenuItem value="aggressive">Aggressive - Maximize savings, risk relationship</MenuItem>
                  <MenuItem value="balanced">Balanced - Fair deal for both parties</MenuItem>
                  <MenuItem value="collaborative">Collaborative - Preserve long-term relationship</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="BATNA (Best Alternative to Negotiated Agreement)"
                placeholder="e.g., Alternative supplier XYZ can provide at $140/unit"
                multiline
                rows={2}
                value={newNegotiation.batna}
                onChange={(e) => setNewNegotiation({ ...newNegotiation, batna: e.target.value })}
                sx={{ mb: 3 }}
                helperText="What's your backup plan if this negotiation fails? This helps the AI determine your leverage."
              />

              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>What is BATNA?</AlertTitle>
                Your Best Alternative to Negotiated Agreement. This is your fallback option if you can't reach a deal. 
                A strong BATNA gives you more leverage. Examples: alternative supplier, make vs buy, delay purchase.
              </Alert>
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Review your negotiation setup:</Typography>
              
              <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Supplier</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {mockSuppliers.find(s => s.id === Number(newNegotiation.supplier_id))?.name || 'Not selected'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Strategy</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {newNegotiation.strategy.charAt(0).toUpperCase() + newNegotiation.strategy.slice(1)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">BATNA</Typography>
                    <Typography variant="body1">{newNegotiation.batna || 'Not specified'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Alert severity="success">
                <AlertTitle>Ready to Start</AlertTitle>
                The AI will analyze supplier history, market conditions, and your targets to recommend optimal negotiation tactics.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Box sx={{ flexGrow: 1 }} />
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(activeStep - 1)}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={() => setActiveStep(activeStep + 1)}>
              Next
            </Button>
          ) : (
            <Button 
              variant="contained" 
              color="success" 
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
              onClick={handleStartAINegotiation}
              disabled={isLoading}
            >
              {isLoading ? 'Running AI Negotiation...' : 'Start AI Negotiation'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Session Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedSession && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Handshake color="primary" />
                  {selectedSession.id} - {selectedSession.supplier_name}
                </Box>
                <Chip 
                  icon={getStatusIcon(selectedSession.status)} 
                  label={selectedSession.status} 
                  color={getStatusColor(selectedSession.status)} 
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        <AttachMoney sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Pricing Summary
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Current</TableCell>
                            <TableCell align="right">Target</TableCell>
                            <TableCell align="right">Qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedSession.products.map((p, i) => (
                            <TableRow key={i}>
                              <TableCell>{p.product_name}</TableCell>
                              <TableCell align="right">${p.current_price}</TableCell>
                              <TableCell align="right" sx={{ color: 'success.main' }}>${p.target_price}</TableCell>
                              <TableCell align="right">{p.quantity}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Target Savings:</Typography>
                        <Typography fontWeight={600} color="primary.main">
                          ${selectedSession.target_savings.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography>Achieved So Far:</Typography>
                        <Typography fontWeight={600} color="success.main">
                          ${selectedSession.actual_savings.toLocaleString()}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        <Psychology sx={{ verticalAlign: 'middle', mr: 1 }} />
                        AI Insights
                      </Typography>
                      {selectedSession.ai_recommendation && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <AlertTitle>Recommendation</AlertTitle>
                          {selectedSession.ai_recommendation}
                        </Alert>
                      )}
                      <List dense>
                        <ListItem>
                          <ListItemIcon><Lightbulb color="warning" /></ListItemIcon>
                          <ListItemText 
                            primary="Supplier's typical discount range: 8-12%"
                            secondary="Based on historical data"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Assessment color="info" /></ListItemIcon>
                          <ListItemText 
                            primary="Your leverage score: 7.2/10"
                            secondary="Volume and alternatives give strong position"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><Schedule color="success" /></ListItemIcon>
                          <ListItemText 
                            primary="Best time to push: Now"
                            secondary="Supplier's quarter-end in 2 weeks"
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        <History sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Negotiation History
                      </Typography>
                      <Stepper alternativeLabel>
                        {[...Array(selectedSession.rounds_completed + 1)].map((_, i) => (
                          <Step key={i} completed={i < selectedSession.rounds_completed}>
                            <StepLabel>
                              Round {i + 1}
                              {i < selectedSession.rounds_completed && (
                                <Typography variant="caption" display="block">Countered</Typography>
                              )}
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
              <Box sx={{ flexGrow: 1 }} />
              {selectedSession.status === 'active' && (
                <>
                  <Button variant="outlined" color="warning">
                    Pause Negotiation
                  </Button>
                  <Button variant="contained" startIcon={<PlayArrow />}>
                    Execute Next Round
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* AI Negotiation Result Dialog */}
      <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology color="primary" />
            AI Negotiation Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {apiResult && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Status Card */}
              <Grid item xs={12}>
                <Alert 
                  severity={apiResult.status === 'completed' ? 'success' : apiResult.status === 'failed' ? 'warning' : 'info'}
                  icon={apiResult.status === 'completed' ? <CheckCircle /> : <Info />}
                >
                  <AlertTitle>
                    Session: {apiResult.session_id.slice(0, 8)}...
                  </AlertTitle>
                  Status: <strong>{apiResult.status.toUpperCase()}</strong> | 
                  Rounds: {apiResult.rounds_count} | 
                  Confidence: {Math.round(apiResult.confidence_score * 100)}%
                </Alert>
              </Grid>

              {/* Pricing Summary */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      <AttachMoney sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Pricing Analysis
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="text.secondary">Initial Price:</Typography>
                      <Typography fontWeight={600}>${apiResult.initial_price.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="text.secondary">Target Price:</Typography>
                      <Typography fontWeight={600} color="primary.main">${apiResult.target_price.toFixed(2)}</Typography>
                    </Box>
                    {apiResult.final_price && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="text.secondary">Final Price:</Typography>
                        <Typography fontWeight={600} color="success.main">${apiResult.final_price.toFixed(2)}</Typography>
                      </Box>
                    )}
                    {apiResult.price_reduction_pct && (
                      <Chip 
                        icon={<TrendingDown />} 
                        label={`${apiResult.price_reduction_pct.toFixed(1)}% reduction`} 
                        color="success" 
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Next Steps */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      <Lightbulb sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Recommended Next Steps
                    </Typography>
                    <List dense>
                      {apiResult.next_steps.map((step, i) => (
                        <ListItem key={i}>
                          <ListItemIcon>
                            <Chip label={i + 1} size="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={step} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Email Draft */}
              {apiResult.email_draft && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        <Email sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Draft Email to Supplier
                      </Typography>
                      <Paper 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'grey.50', 
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.875rem'
                        }}
                      >
                        {apiResult.email_draft}
                      </Paper>
                      <Button 
                        variant="outlined" 
                        startIcon={<Email />} 
                        sx={{ mt: 2 }}
                        onClick={() => {
                          navigator.clipboard.writeText(apiResult.email_draft);
                          setSnackbar({ open: true, message: 'Email copied to clipboard!', severity: 'success' });
                        }}
                      >
                        Copy Email
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResultDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Running LangGraph AI Negotiation...
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.300', mt: 1 }}>
            Analyzing supplier behavior and generating strategy
          </Typography>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NegotiationsPage;
